import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Gift, KeyRound, MailCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const Shell = ({ children }) => (
  <div className="min-h-screen bg-background flex items-center justify-center px-4">
    <div className="w-full max-w-md fade-up">
      <Link to="/" className="flex items-center gap-2 justify-center mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center"><Gift size={22} className="text-white" /></div>
        <span className="font-heading font-bold text-2xl">hediyeniiste</span>
      </Link>
      <div className="bg-card rounded-2xl border border-border/50 shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-8">{children}</div>
    </div>
  </div>
);

export default function PasswordPages() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLang();
  const token = searchParams.get("token");
  const isReset = location.pathname === "/sifre-sifirla" && token;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("pw_updated"));
      navigate("/giris");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("error_generic"));
    } finally { setBusy(false); }
  };

  if (isReset) {
    return (
      <Shell>
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4"><KeyRound size={20} className="text-primary" /></div>
        <h1 className="font-heading text-2xl font-bold mb-1">{t("reset_t")}</h1>
        <p className="text-sm text-muted-foreground mb-6">{t("reset_d")}</p>
        <form onSubmit={doReset} className="space-y-4">
          <div>
            <Label>{t("lbl_new_pw")}</Label>
            <Input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1 rounded-xl h-11" placeholder="••••••••" data-testid="reset-password-input" />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 font-semibold" data-testid="reset-password-btn">
            {busy ? t("saving") : t("update_pw")}
          </Button>
        </form>
      </Shell>
    );
  }

  return (
    <Shell>
      {done ? (
        <div className="text-center py-6" data-testid="forgot-success">
          <p className="text-4xl mb-4">📬</p>
          <h1 className="font-heading text-xl font-bold mb-2">{t("check_mail_t")}</h1>
          <p className="text-sm text-muted-foreground">{t("check_mail_d")}</p>
          <Link to="/giris" className="text-primary font-semibold text-sm mt-4 inline-block">{t("back_login")}</Link>
        </div>
      ) : (
        <>
          <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center mb-4"><MailCheck size={20} className="text-primary" /></div>
          <h1 className="font-heading text-2xl font-bold mb-1">{t("forgot_t")}</h1>
          <p className="text-sm text-muted-foreground mb-6">{t("forgot_d")}</p>
          <form onSubmit={requestReset} className="space-y-4">
            <div>
              <Label>{t("email_label")}</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 rounded-xl h-11" placeholder="ornek@mail.com" data-testid="forgot-email-input" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-full h-11 font-semibold" data-testid="forgot-submit-btn">
              {busy ? t("sending") : t("send_reset")}
            </Button>
          </form>
        </>
      )}
    </Shell>
  );
}

export function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState("loading");
  const { t } = useLang();
  const called = useRef(false);

  useEffect(() => {
    if (called.current) return;
    called.current = true;
    const token = searchParams.get("token");
    if (!token) { setState("error"); return; }
    api.post("/auth/verify-email", { token })
      .then(() => setState("ok"))
      .catch(() => setState("error"));
  }, [searchParams]);

  return (
    <Shell>
      <div className="text-center py-6" data-testid="verify-page">
        {state === "loading" && <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />}
        {state === "ok" && (
          <>
            <p className="text-4xl mb-4">✅</p>
            <h1 className="font-heading text-xl font-bold mb-2" data-testid="verify-success">{t("verify_ok_t")}</h1>
            <p className="text-sm text-muted-foreground mb-4">{t("verify_ok_d")}</p>
            <Link to="/dashboard" className="text-primary font-semibold text-sm">{t("go_panel")}</Link>
          </>
        )}
        {state === "error" && (
          <>
            <p className="text-4xl mb-4">⚠️</p>
            <h1 className="font-heading text-xl font-bold mb-2" data-testid="verify-error">{t("verify_err_t")}</h1>
            <p className="text-sm text-muted-foreground mb-4">{t("verify_err_d")}</p>
            <Link to="/ayarlar" className="text-primary font-semibold text-sm">{t("go_settings")}</Link>
          </>
        )}
      </div>
    </Shell>
  );
}
